// Shared form widgets used by both the create-task and edit-task modals.
// Keep pieces used by more than one task modal here so neither modal
// depends on the other's file.
import { NumberField } from "@/components/ui/number-field";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Trash2, GripVertical, Search, CheckSquare } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type Control, type UseFormReturn } from "react-hook-form";
import {
  type PendingSubTask, type FriendWithUser, type AssistantWithUser,
  type TaskFormInput, type TaskFormData,
} from "./task-types";

// ── Friend search ──────────────────────────────────────────────────────────

// Helper function for flexible friend search
export function matchesSearch(searchTerm: string, firstName: string, lastName: string, email: string): boolean {
  if (!searchTerm.trim()) return true;

  const search = searchTerm.toLowerCase().trim();
  const first = (firstName || '').toLowerCase();
  const last = (lastName || '').toLowerCase();
  const fullName = `${first} ${last}`;
  const combinedName = `${first}${last}`;
  const emailLower = (email || '').toLowerCase();

  if (first.includes(search) || last.includes(search) || fullName.includes(search) || emailLower.includes(search)) {
    return true;
  }

  return combinedName.includes(search);
}

// Filter friends by search query
export function useFilteredFriends(friends: FriendWithUser[], friendSearchQuery: string) {
  return useMemo(() => {
    return friends.filter(friendship => matchesSearch(
      friendSearchQuery,
      friendship.friend.firstName || '',
      friendship.friend.lastName || '',
      friendship.friend.email || ''
    ));
  }, [friends, friendSearchQuery]);
}

// "Select a Friend" block shared by both task modals.
export function FriendPicker({
  friends,
  friendSearchQuery,
  onFriendSearchQueryChange,
  selectedFriendId,
  onSelectFriend,
  friendTestIdPrefix,
  selectedHint,
}: {
  friends: FriendWithUser[];
  friendSearchQuery: string;
  onFriendSearchQueryChange: (q: string) => void;
  selectedFriendId: string | null;
  onSelectFriend: (id: string) => void;
  friendTestIdPrefix: string;
  selectedHint: string;
}) {
  const filteredFriends = useFilteredFriends(friends, friendSearchQuery);

  return (
    <div className="space-y-3">
      <FormLabel>Select a Friend</FormLabel>
      {friends.length > 0 ? (
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search friends by name or email..."
              value={friendSearchQuery}
              onChange={(e) => onFriendSearchQueryChange(e.target.value)}
              className="pl-9"
              data-testid="input-search-friends"
            />
          </div>
          <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
            {filteredFriends.map((friendship) => {
              const friend = friendship.friend;
              const name = friend.firstName && friend.lastName
                ? `${friend.firstName} ${friend.lastName}`
                : friend.username || friend.email || "Unknown";
              const isSelected = selectedFriendId === friend.id;
              return (
                <div
                  key={friendship.id}
                  className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                    isSelected ? 'bg-primary/10 border border-primary' : 'hover:bg-muted'
                  }`}
                  onClick={() => onSelectFriend(friend.id)}
                  data-testid={`${friendTestIdPrefix}-${friend.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-medium">
                    {(friend.firstName?.[0] || friend.email?.[0] || "?").toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{name}</div>
                    <div className="text-xs text-muted-foreground">{friend.email || "Friend"}</div>
                  </div>
                  {isSelected && <CheckSquare className="w-4 h-4 text-primary" />}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground border rounded-lg p-4 text-center">
          No friends available. Add friends in the Community section to assign tasks to them.
        </div>
      )}
      {selectedFriendId && (
        <p className="text-xs text-muted-foreground">
          {selectedHint}
        </p>
      )}
    </div>
  );
}

// ── Assign To select ───────────────────────────────────────────────────────

export type AssignmentType = "Me" | "Friend" | "Assistant" | "Other";

export type ResolvedTaskAssignment = {
  assignedTo: "Me" | "Others";
  assignedToOther: string | null;
  assignedToUserId: string | null;
};

// Resolve the assignedTo/assignedToOther/assignedToUserId trio submitted with
// a task from the modal's assignment UI state. Shared by the create and edit
// task modals so both submit paths guard identically:
// - "Friend" / "Assistant" with no (or unknown) selection falls back to "Me".
// - "Other" with a blank/whitespace-only name falls back to "Me", so a task
//   can never be saved as assigned to "Others" with nobody named.
export function resolveTaskAssignment({
  assignmentType,
  selectedFriendId,
  friends,
  selectedAssistantId,
  assistants,
  assignedToOther,
}: {
  assignmentType: AssignmentType;
  selectedFriendId: string | null;
  friends: FriendWithUser[];
  selectedAssistantId?: string | null;
  assistants?: AssistantWithUser[];
  assignedToOther: string | undefined;
}): ResolvedTaskAssignment {
  if (assignmentType === "Friend" && selectedFriendId) {
    const selectedFriend = friends.find(f => f.friend.id === selectedFriendId);
    if (selectedFriend) {
      const friendName = selectedFriend.friend.firstName && selectedFriend.friend.lastName
        ? `${selectedFriend.friend.firstName} ${selectedFriend.friend.lastName}`
        : selectedFriend.friend.username || selectedFriend.friend.email || "Friend";
      return { assignedTo: "Others", assignedToOther: friendName, assignedToUserId: selectedFriendId };
    }
  } else if (assignmentType === "Assistant" && selectedAssistantId && assistants) {
    const selectedAssistant = assistants.find(a => a.user.id === selectedAssistantId);
    if (selectedAssistant) {
      const assistantName = selectedAssistant.user.firstName && selectedAssistant.user.lastName
        ? `${selectedAssistant.user.firstName} ${selectedAssistant.user.lastName}`
        : selectedAssistant.user.username || selectedAssistant.user.email || "Assistant";
      return { assignedTo: "Others", assignedToOther: assistantName, assignedToUserId: selectedAssistantId };
    }
  } else if (assignmentType === "Other") {
    const name = (assignedToOther || "").trim();
    if (name) {
      return { assignedTo: "Others", assignedToOther: name, assignedToUserId: null };
    }
  }
  return { assignedTo: "Me", assignedToOther: null, assignedToUserId: null };
}

// "Assign To" (Me / Friend / Assistant / Other Person) select shared by both
// task modals, including the on-change reset of sub-pickers so a behavioral
// change here can't miss one of the modals.
export function AssignToSelect({
  assignmentType,
  onAssignmentTypeChange,
  onSelectedFriendIdChange,
  onFriendSearchQueryChange,
  onSelectedAssistantIdChange,
  hasAssistants,
  form,
  testId,
}: {
  assignmentType: AssignmentType;
  onAssignmentTypeChange: (v: AssignmentType) => void;
  onSelectedFriendIdChange: (id: string | null) => void;
  onFriendSearchQueryChange: (q: string) => void;
  onSelectedAssistantIdChange?: (id: string | null) => void;
  /** Whether the current user has at least one assistant — hides the option when false. */
  hasAssistants?: boolean;
  form: UseFormReturn<TaskFormInput, any, TaskFormData>;
  /** "select-assigned-to" (create) or "select-edit-assigned-to" (edit). */
  testId: string;
}) {
  return (
    <div className="space-y-2">
      <FormLabel>Assign To</FormLabel>
      <Select value={assignmentType} onValueChange={(v) => {
        const value = v as AssignmentType;
        onAssignmentTypeChange(value);
        if (value === "Me") {
          onSelectedFriendIdChange(null);
          onFriendSearchQueryChange("");
          onSelectedAssistantIdChange?.(null);
          form.setValue("assignedToOther", undefined);
        } else if (value === "Friend") {
          onSelectedAssistantIdChange?.(null);
          form.setValue("assignedToOther", undefined);
        } else if (value === "Assistant") {
          onSelectedFriendIdChange(null);
          onFriendSearchQueryChange("");
          form.setValue("assignedToOther", undefined);
        } else if (value === "Other") {
          onSelectedFriendIdChange(null);
          onFriendSearchQueryChange("");
          onSelectedAssistantIdChange?.(null);
        }
      }}>
        <SelectTrigger data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Me">Me</SelectItem>
          <SelectItem value="Friend">Friend</SelectItem>
          {hasAssistants && <SelectItem value="Assistant">Assistant</SelectItem>}
          <SelectItem value="Other">Other Person</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

// "Select an Assistant" block shared by both task modals.
export function AssistantPicker({
  assistants,
  selectedAssistantId,
  onSelectAssistant,
  testIdPrefix,
}: {
  assistants: AssistantWithUser[];
  selectedAssistantId: string | null;
  onSelectAssistant: (userId: string) => void;
  testIdPrefix: string;
}) {
  return (
    <div className="space-y-3">
      <FormLabel>Select an Assistant</FormLabel>
      {assistants.length > 0 ? (
        <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
          {assistants.map((assistant) => {
            const name = assistant.user.firstName && assistant.user.lastName
              ? `${assistant.user.firstName} ${assistant.user.lastName}`
              : assistant.user.username || assistant.user.email || "Assistant";
            const isSelected = selectedAssistantId === assistant.user.id;
            return (
              <div
                key={assistant.id}
                className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                  isSelected ? 'bg-primary/10 border border-primary' : 'hover:bg-muted'
                }`}
                onClick={() => onSelectAssistant(assistant.user.id)}
                data-testid={`${testIdPrefix}-${assistant.user.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-sm font-medium">
                  {(assistant.user.firstName?.[0] || assistant.user.email?.[0] || "A").toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">{name}</div>
                  <div className="text-xs text-muted-foreground capitalize">
                    {assistant.assistantType} · {assistant.user.email || ""}
                  </div>
                </div>
                {isSelected && <CheckSquare className="w-4 h-4 text-primary" />}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground border rounded-lg p-4 text-center">
          No assistants available. Add an assistant in Settings to assign tasks to them.
        </div>
      )}
    </div>
  );
}

// Conditional "Person's Name" input shown when Assign To = Other, shared by
// both task modals so a behavioral/validation change can't miss one of them.
export function AssignedToOtherField({
  control,
  testId,
}: {
  control: Control<TaskFormInput, any, TaskFormData>;
  /** "input-assigned-to-other" (create) or "input-edit-assigned-to-other" (edit). */
  testId: string;
}) {
  return (
    <FormField
      control={control}
      name="assignedToOther"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Person's Name</FormLabel>
          <FormControl>
            <Input
              {...field}
              value={field.value || ""}
              placeholder="Enter the person's name"
              data-testid={testId}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// ── Tag editor ─────────────────────────────────────────────────────────────

export function TagEditor({
  tags,
  onTagsChange,
  currentTag,
  onCurrentTagChange,
  inputTestId,
  addButtonTestId,
  removeTagTestIdPrefix,
}: {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  currentTag: string;
  onCurrentTagChange: (tag: string) => void;
  inputTestId: string;
  addButtonTestId: string;
  removeTagTestIdPrefix: string;
}) {
  const addTag = () => {
    if (currentTag.trim() && !tags.includes(currentTag.trim())) {
      onTagsChange([...tags, currentTag.trim()]);
      onCurrentTagChange("");
    }
  };

  return (
    <div className="space-y-2">
      <FormLabel>Tags</FormLabel>
      <div className="flex gap-2">
        <Input
          value={currentTag}
          onChange={(e) => onCurrentTagChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder="Type tag and press Enter"
          data-testid={inputTestId}
        />
        <Button
          type="button"
          variant="outline"
          onClick={addTag}
          data-testid={addButtonTestId}
        >
          Add
        </Button>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="gap-1">
              {tag}
              <button
                type="button"
                onClick={() => onTagsChange(tags.filter((t) => t !== tag))}
                className="ml-1 hover:text-destructive"
                data-testid={`${removeTagTestIdPrefix}-${tag}`}
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Recurrence section ─────────────────────────────────────────────────────

export function RecurrenceSection({
  isRecurring,
  onToggleRecurring,
  recurrenceFrequency,
  onRecurrenceFrequencyChange,
  recurrenceEndType,
  onRecurrenceEndTypeChange,
  recurrenceCount,
  onRecurrenceCountChange,
  recurrenceEndDate,
  onRecurrenceEndDateChange,
  frequencyOptions,
  idSuffix = "",
  children,
}: {
  isRecurring: boolean;
  onToggleRecurring: () => void;
  recurrenceFrequency: string;
  onRecurrenceFrequencyChange: (v: string) => void;
  recurrenceEndType: "count" | "date";
  onRecurrenceEndTypeChange: (v: "count" | "date") => void;
  recurrenceCount: number | undefined;
  onRecurrenceCountChange: (v: number | undefined) => void;
  recurrenceEndDate: string;
  onRecurrenceEndDateChange: (v: string) => void;
  frequencyOptions: { value: string; label: string }[];
  /** "" for the create modal, "-edit" for the edit modal (keeps existing ids/testids stable). */
  idSuffix?: string;
  /** Extra content rendered inside the expanded recurring block (e.g. occurrences list). */
  children?: React.ReactNode;
}) {
  return (
    <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
      <div className="flex items-center justify-between">
        <FormLabel>Recurring Task</FormLabel>
        <Button
          type="button"
          variant={isRecurring ? "default" : "outline"}
          size="sm"
          onClick={onToggleRecurring}
          data-testid={`button-toggle-recurring${idSuffix}`}
        >
          {isRecurring ? "Enabled" : "Enable Recurring"}
        </Button>
      </div>

      {isRecurring && (
        <div className="space-y-4 mt-3">
          <div className="space-y-2">
            <FormLabel>Frequency</FormLabel>
            <Select value={recurrenceFrequency} onValueChange={onRecurrenceFrequencyChange}>
              <SelectTrigger data-testid={`select-recurrence-frequency${idSuffix}`}>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                {frequencyOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className={idSuffix ? "space-y-2" : "space-y-3"}>
            <FormLabel>End Condition</FormLabel>
            <RadioGroup value={recurrenceEndType} onValueChange={(v) => onRecurrenceEndTypeChange(v as "count" | "date")}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="count" id={`count${idSuffix}`} data-testid={`radio-end-count${idSuffix}`} />
                <label htmlFor={`count${idSuffix}`} className="cursor-pointer">Number of occurrences</label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="date" id={`date${idSuffix}`} data-testid={`radio-end-date${idSuffix}`} />
                <label htmlFor={`date${idSuffix}`} className="cursor-pointer">End date</label>
              </div>
            </RadioGroup>
          </div>

          {recurrenceEndType === "count" ? (
            <div className="space-y-2">
              <FormLabel>Number of Occurrences</FormLabel>
              <NumberField
                min={1}
                max={100}
                value={recurrenceCount}
                onValueChange={onRecurrenceCountChange}
                placeholder="Enter number of occurrences"
                data-testid={`input-recurrence-count${idSuffix}`}
              />
              <p className="text-xs text-muted-foreground">
                {recurrenceCount ? `This will create ${recurrenceCount} tasks` : 'Enter a number to see preview'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <FormLabel>End Date</FormLabel>
              <Input
                type="date"
                value={recurrenceEndDate}
                onChange={(e) => onRecurrenceEndDateChange(e.target.value)}
                data-testid={`input-recurrence-end-date${idSuffix}`}
              />
              <p className="text-xs text-muted-foreground">
                Tasks will be created until this date
              </p>
            </div>
          )}

          <div className="p-3 bg-primary/5 rounded border border-primary/20">
            <p className="text-sm font-medium text-primary mb-1">Recurrence Summary:</p>
            <p className="text-xs text-muted-foreground">
              Creating <strong>{recurrenceFrequency}</strong> tasks
              {recurrenceEndType === "count"
                ? recurrenceCount ? ` for ${recurrenceCount} occurrence${recurrenceCount > 1 ? 's' : ''}` : ''
                : recurrenceEndDate
                  ? ` until ${new Date(recurrenceEndDate).toLocaleDateString()}`
                  : ''}
            </p>
          </div>

          {children}
        </div>
      )}
    </div>
  );
}

// ── Link entity select field ───────────────────────────────────────────────

// Shared FormField-based select used for the goal / milestone / journal link
// pickers in both modals.
export function LinkEntitySelectField({
  control,
  name,
  label,
  placeholder,
  testId,
  options,
  emptyMessage,
}: {
  control: Control<TaskFormInput, any, TaskFormData>;
  name: "goalId" | "milestoneId" | "journalEntryId";
  label: string;
  placeholder: string;
  testId: string;
  options: { id: string; title: string; subtitle?: string }[];
  /** Rendered inside the dropdown when there are no options. */
  emptyMessage?: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Select onValueChange={field.onChange} value={field.value || undefined}>
            <FormControl>
              <SelectTrigger data-testid={testId}>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.length === 0 && emptyMessage ? (
                <div className="p-3 text-sm text-muted-foreground text-center">
                  {emptyMessage}
                </div>
              ) : (
                options.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.title}
                    {option.subtitle && (
                      <span className="text-muted-foreground ml-1 text-xs">({option.subtitle})</span>
                    )}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export function SortablePendingSubTaskItem({
  subTask,
  onTextChange,
  onDateChange,
  onDelete,
}: {
  subTask: PendingSubTask;
  onTextChange: (id: string, text: string) => void;
  onDateChange: (id: string, date: string | null) => void;
  onDelete: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: subTask.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-col sm:flex-row sm:items-center gap-2 p-2 sm:p-3 border rounded bg-card"
      data-testid={`pending-subtask-item-${subTask.id}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="w-5 text-center text-xs font-mono font-semibold text-muted-foreground flex-shrink-0 select-none">
          {subTask.rowNumber}
        </span>

        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 flex-shrink-0"
          data-testid={`drag-handle-pending-subtask-${subTask.id}`}
        >
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>

        <Input
          value={subTask.text}
          onChange={(e) => onTextChange(subTask.id, e.target.value)}
          className="flex-1 min-w-0"
          placeholder="Sub-task name"
          data-testid={`input-pending-subtask-text-${subTask.id}`}
        />
      </div>

      <div className="flex items-center gap-2 sm:flex-shrink-0">
        <Input
          type="date"
          value={subTask.dueDate || ''}
          onChange={(e) => onDateChange(subTask.id, e.target.value || null)}
          className="flex-1 sm:w-40"
          data-testid={`input-pending-subtask-date-${subTask.id}`}
        />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onDelete(subTask.id)}
          className="text-destructive flex-shrink-0"
          data-testid={`button-delete-pending-subtask-${subTask.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
